import { Dispatch } from '@reduxjs/toolkit';
import { keyBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { AccountAction, AccountActionAttributes } from 'dashboard/models/account-action';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { setWorkflow, setWorkflowDeployment } from 'dashboard/redux/actions/workflow';
import { setAccountAction } from 'dashboard/redux/slices/account-action';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel, ApiSingletonPayload } from 'lib/interfaces/api';

export const loadAccountAction = (accountActionId: string): DashboardThunkAction<Promise<void>> => {
  return async (dispatch: Dispatch) => {
    const { data: actionData, included } = await SessionClientDashboard.getResource<AccountAction>(
      ApiEndpoint.AccountAction,
      accountActionId,
    );

    const includedItemsMap = keyBy('id', included);

    const actionWorkflow =
      actionData.relationships.workflow?.data?.id != null
        ? (includedItemsMap[actionData.relationships.workflow?.data?.id] as Workflow)
        : undefined;

    const actionWorkflowDeployment =
      actionWorkflow?.relationships?.['active-deployment']?.data != null
        ? (includedItemsMap[
            actionWorkflow.relationships['active-deployment'].data.id
          ] as WorkflowDeployment)
        : undefined;

    dispatch(setAccountAction({ accountAction: actionData }));
    actionWorkflow && dispatch(setWorkflow(actionWorkflow));
    actionWorkflowDeployment && dispatch(setWorkflowDeployment(actionWorkflowDeployment));
  };
};

export const updateAccountActionName = (
  name: string,
  accountActionId: string,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch: Dispatch) => {
    const { data, included } = await SessionClientDashboard.updateResource<AccountAction>({
      type: ApiModel.AccountAction,
      id: accountActionId,
      attributes: {
        name: name,
      },
    });

    const includedItemsMap = keyBy('id', included);
    const actionWorkflow =
      data.relationships.workflow?.data?.id != null
        ? (includedItemsMap[data.relationships.workflow.data.id] as Workflow)
        : undefined;

    dispatch(setAccountAction({ accountAction: data }));
    actionWorkflow && dispatch(setWorkflow(actionWorkflow));
  };
};

export function createAccountAction(accountTypeId: string, name: string) {
  return async () => {
    return await SessionClientDashboard.postResourceCreate<
      AccountAction,
      ApiSingletonPayload<Partial<AccountActionAttributes> & { 'account-type-id': string }>
    >({
      type: ApiModel.AccountAction,
      attributes: {
        name,
        'account-type-id': accountTypeId,
      },
    });
  };
}

export const activateAccountAction = (
  accountActionId: string,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { data, included } = await SessionClientDashboard.postResourceAction<AccountAction>(
      { id: accountActionId, type: ApiModel.AccountAction },
      'activate',
    );

    const includedItemsMap = keyBy('id', included);
    const actionWorkflow =
      data.relationships.workflow?.data?.id != null
        ? (includedItemsMap[data.relationships.workflow.data.id] as Workflow)
        : undefined;

    dispatch(setAccountAction({ accountAction: data }));
    actionWorkflow && dispatch(setWorkflow(actionWorkflow));
  };
};

export const deactivateAccountAction = (
  accountActionId: string,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { data, included } = await SessionClientDashboard.postResourceAction<AccountAction>(
      { id: accountActionId, type: ApiModel.AccountAction },
      'deactivate',
    );

    const includedItemsMap = keyBy('id', included);
    const actionWorkflow =
      data.relationships.workflow?.data?.id != null
        ? (includedItemsMap[data.relationships.workflow.data.id] as Workflow)
        : undefined;

    dispatch(setAccountAction({ accountAction: data }));
    actionWorkflow && dispatch(setWorkflow(actionWorkflow));
  };
};

export function archiveAccountAction(accountActionId: string) {
  return async () => {
    await SessionClientDashboard.deleteResource<AccountAction>(
      ApiEndpoint.AccountAction,
      accountActionId,
    );
  };
}

export function restoreAccountActionDraft(
  accountActionId: string,
  workflowVersionId: string,
): DashboardThunkAction<Promise<{ actionWorkflow?: Workflow }>> {
  return async (dispatch) => {
    const { data, included } = await SessionClientDashboard.postResourceAction<AccountAction>(
      { id: accountActionId, type: ApiModel.AccountAction },
      'restore_draft',
      { meta: { 'workflow-version-id': workflowVersionId } },
    );

    const includedItemsMap = keyBy('id', included);
    const actionWorkflow =
      data.relationships.workflow?.data?.id != null
        ? (includedItemsMap[data.relationships.workflow.data.id] as Workflow)
        : undefined;

    dispatch(setAccountAction({ accountAction: data }));
    actionWorkflow && dispatch(setWorkflow(actionWorkflow));

    return { actionWorkflow };
  };
}
